import { useEffect, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { useTimezone } from "hooks/useTimezone"

import { BetbyBonusRecord, BetbyBonusService } from "services/betbyBonus/betbyBonus.service"

const STATUS_COLORS: Record<string, string> = {
  ISSUED: "bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100",
  ACTIVE: "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100",
  USED: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
  EXPIRED: "bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400",
  REVOKED: "bg-orange-100 text-orange-800 dark:bg-orange-800 dark:text-orange-100",
  ERROR: "bg-red-100 text-red-800 dark:bg-red-800 dark:text-red-100"
}

const STATUS_OPTIONS = ["", "ISSUED", "ACTIVE", "USED", "EXPIRED", "REVOKED", "ERROR"]

export function IssuedBonusesTable() {
  const { formatDateTime } = useTimezone()

  function formatDate(iso: string | null) {
    if (!iso) return "—"
    return formatDateTime(iso)
  }
  function formatAmount(cents: number, currency: string) {
    return `$${(cents / 100).toFixed(2)} ${currency}`
  }
  const [items, setItems] = useState<BetbyBonusRecord[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [filterStatus, setFilterStatus] = useState("")
  const [filterUserId, setFilterUserId] = useState("")
  const [revoking, setRevoking] = useState<string | null>(null)
  const pageSize = 25

  const fetchData = async () => {
    setLoading(true)
    const result = await BetbyBonusService.listBonuses({
      page,
      pageSize,
      status: filterStatus || undefined,
      userId: filterUserId || undefined
    })
    setItems(result.items)
    setTotal(result.total)
    setLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [page, filterStatus, filterUserId])

  const handleRevoke = async (row: BetbyBonusRecord) => {
    if (!row.betby_bonus_id) {
      toast.error("No BetBy bonus ID — cannot revoke")
      return
    }
    const confirmed = window.confirm(`Revoke bonus ${row.betby_bonus_id} for ${row.user?.email_address ?? row.user?.id}?`)
    if (!confirmed) return
    setRevoking(row.id)
    const ok = await BetbyBonusService.revokeBonus([row.betby_bonus_id])
    setRevoking(null)
    if (ok) {
      toast.success("Bonus revoked")
      fetchData()
    } else {
      toast.error("Failed to revoke bonus")
    }
  }

  const totalPages = Math.ceil(total / pageSize)

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Status</label>
          <select
            className="px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            value={filterStatus}
            onChange={(e) => {
              setFilterStatus(e.target.value)
              setPage(1)
            }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s || "All statuses"}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">User ID</label>
          <input
            type="text"
            placeholder="Filter by user ID…"
            className="px-3 py-1.5 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 dark:text-white w-64"
            value={filterUserId}
            onChange={(e) => {
              setFilterUserId(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div className="flex items-end">
          <span className="text-xs text-gray-500">{total} total records</span>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-500 py-8 text-center">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-gray-500 py-8 text-center">No bonuses found</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b dark:border-gray-700 text-left text-xs uppercase text-gray-500">
                  <th className="py-2 pr-4">Player</th>
                  <th className="py-2 pr-4">Template</th>
                  <th className="py-2 pr-4">Amount</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Issued</th>
                  <th className="py-2 pr-4">Expires</th>
                  <th className="py-2 pr-4">Activated</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr key={row.id} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="py-2 pr-4">
                      <div className="text-xs font-medium text-gray-900 dark:text-white">{row.user?.email_address ?? "—"}</div>
                      <div className="text-xs text-gray-400 font-mono">{row.user?.id}</div>
                    </td>
                    <td className="py-2 pr-4 text-xs">
                      <div className="text-gray-700 dark:text-gray-300">{row.template_name ?? "—"}</div>
                      <div className="text-gray-400 font-mono">{row.bonus_type}</div>
                    </td>
                    <td className="py-2 pr-4 text-xs text-gray-700 dark:text-gray-300">{formatAmount(row.amount_cents, row.currency)}</td>
                    <td className="py-2 pr-4">
                      <span className={clsx("px-2 py-0.5 rounded-full text-xs font-medium", STATUS_COLORS[row.status] ?? "")}>
                        {row.status}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-xs text-gray-500">{formatDate(row.issued_at)}</td>
                    <td className="py-2 pr-4 text-xs text-gray-500">{formatDate(row.expires_at)}</td>
                    <td className="py-2 pr-4 text-xs text-gray-500">{formatDate(row.activated_at)}</td>
                    <td className="py-2">
                      {(row.status === "ISSUED" || row.status === "ACTIVE") && (
                        <button
                          onClick={() => handleRevoke(row)}
                          disabled={revoking === row.id}
                          className="px-3 py-1 text-xs bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-800 dark:hover:bg-red-700 dark:text-red-100 rounded disabled:opacity-50"
                        >
                          {revoking === row.id ? "Revoking…" : "Revoke"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center gap-2 mt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border rounded disabled:opacity-40 dark:border-gray-600 dark:text-white"
              >
                Prev
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm border rounded disabled:opacity-40 dark:border-gray-600 dark:text-white"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
